/**
 * 권한 상수 정의
 * Feature 036: Role 기반 권한 단순화
 *
 * 12개 권한 (SYSTEM 3개, MENU 5개, PROJECT 3개)
 * - 레거시 권한 3개 삭제: MenuGrant, MenuServer, MenuIP
 * - Role 기반 권한 부여: MANAGER_GRANTS (8개), USER_GRANTS (2개)
 * - ICT-30: MenuMeasureDiscusSetting 추가
 */

import { UserCompanyRole } from "./userCompany";

// 전체시스템 권한 (3개)
export const SYSTEM_GRANTS = {
  /** 앱 로그인 무제한 */
  APP_LOGIN_NO_LIMIT: "AppLoginNoLmit",
  /** 다른 법인 접근 */
  OTHER_COMPANY_ACCESS: "OtherCompanyAccess",
  /** 외부접근 가능 */
  IP_NO_LIMIT: "IpLNoimit",
} as const;

// 메뉴 권한 (4개 - 레거시 4개 삭제됨: MenuGrant, MenuServer, MenuIP, MenuDataDiscusSetting)
export const MENU_GRANTS = {
  /** 설비 설정 */
  MACHINE_SETTING: "MenuMacSetting",
  /** 보고서 설정 */
  REPORT_SETTING: "MenuReportSetting",
  /** 계측 코멘트 설정 (ICT-30) */
  MEASURE_DISCUS_SETTING: "MenuMeasureDiscusSetting",
  /** 직원 관리 */
  USER: "MenuUser",
} as const;

// 프로젝트 권한 (3개)
export const PROJECT_GRANTS = {
  /** 프로젝트 생성 */
  CREATE: "ProjectCreate",
  /** 프로젝트 수정 */
  MODIFY: "ProjectModify",
  /** 프로젝트 삭제 */
  DELETE: "ProjectDelete",
} as const;

// 전체 권한 목록 (12개)
export const GRANT_LIST = [
  ...Object.values(SYSTEM_GRANTS),
  ...Object.values(MENU_GRANTS),
  ...Object.values(PROJECT_GRANTS),
] as const;

// 권한 타입
export type Grant = (typeof GRANT_LIST)[number];

// ========== Role 기반 권한 매핑 (Feature 036) ==========

/**
 * Manager 역할 기본 권한 (7개)
 * - 메뉴 권한 4개: 설비설정, 보고서설정, 계측코멘트설정, 직원관리
 * - 프로젝트 권한 3개: 생성, 수정, 삭제
 */
export const MANAGER_GRANTS: readonly Grant[] = [
  MENU_GRANTS.MACHINE_SETTING,
  MENU_GRANTS.REPORT_SETTING,
  MENU_GRANTS.MEASURE_DISCUS_SETTING,
  MENU_GRANTS.USER,
  PROJECT_GRANTS.CREATE,
  PROJECT_GRANTS.MODIFY,
  PROJECT_GRANTS.DELETE,
] as const;

/**
 * User 역할 기본 권한 (2개)
 * - 프로젝트 권한 2개: 생성, 수정 (삭제 권한 없음)
 */
export const USER_GRANTS: readonly Grant[] = [
  PROJECT_GRANTS.CREATE,
  PROJECT_GRANTS.MODIFY,
] as const;

/**
 * Role에 따른 기본 권한 배열 반환
 * @param role - UserCompanyRole (MANAGER 또는 USER)
 * @returns 해당 Role의 권한 배열 (불변성 보장을 위해 복사본 반환)
 */
export function getRoleGrants(role: UserCompanyRole): Grant[] {
  switch (role) {
    case UserCompanyRole.MANAGER:
      return [...MANAGER_GRANTS];
    case UserCompanyRole.USER:
      return [...USER_GRANTS];
    default:
      return [];
  }
}
